"use client";

import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useChat } from "@ai-sdk/react";
import Image from "next/image";
import { TravelPromptSuggestions } from "@/components/TravelPromptSuggestions";
import { DestinationShowcase } from "@/components/DestinationShowCase";
import Header from "@/components/Header";
import { v4 as uuidv4 } from "uuid"; // You'll need to install uuid package

export default function ChatPage() {
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [showWelcome, setShowWelcome] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const destinations = [
    { text: "Paris, France", img: "/chat/square/paris.jpg" },
    { text: "Tokyo, Japan", img: "/chat/square/tokyo.jpg" },
    { text: "Bali, Indonesia", img: "/chat/square/bali.jpg" },
    { text: "New York, USA", img: "/chat/square/nyc.jpg" },
  ];

  // Generate a session ID when component mounts and store it
  const [sessionId, setSessionId] = useState("");

  useEffect(() => {
    // Try to get existing session ID from localStorage
    const existingSessionId = localStorage.getItem("chatSessionId");

    if (existingSessionId) {
      setSessionId(existingSessionId);
    } else {
      // Create new session ID if none exists
      const newSessionId = uuidv4();
      localStorage.setItem("chatSessionId", newSessionId);
      setSessionId(newSessionId);
    }
  }, []);

  const {
    messages,
    input,
    handleInputChange,
    handleSubmit,
    append,
    isLoading,
  } = useChat({
    api: "/api/chat",
    initialMessages: [
      {
        id: "welcome",
        role: "assistant",
        content:
          "👋 Hi there! I'm your AI travel assistant. How can I help plan your next adventure?",
      },
    ],
    body: {
      sessionId: sessionId, // Pass the session ID with each request
    },
    onError: (error) => {
      try {
        const parsed = JSON.parse(error.message);
        setErrorMessage(parsed.error || "Error Occured");
      } catch (e) {
        console.log(e);
        setErrorMessage("Error Occured");
      }
    },
  });

  const handlePromptClick = (promptText: string) => {
    append({
      role: "user",
      content: promptText,
    });
  };

  // Handle welcome state update separately from the main useEffect
  useEffect(() => {
    if (messages.length > 1 && showWelcome) {
      setShowWelcome(false);
    }
  }, [messages.length, showWelcome]);

  // Handle scrolling in a separate useEffect
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (errorMessage) {
      const timeout = setTimeout(() => {
        setErrorMessage("");
      }, 4000);

      return () => clearTimeout(timeout);
    }
  }, [errorMessage]);

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-blue-50 to-indigo-50 dark:bg-gradient-to-br dark:from-gray-900 dark:to-gray-800 dark:text-white">
      <Header />

      {/* Main content */}
      <main className="flex-grow flex flex-col md:flex-row max-w-6xl mx-auto w-full p-4 md:p-6 gap-6 md:pt-24 pt-24">
        {/* Left sidebar - only visible on medium screens and up */}
        <aside className="hidden md:block md:w-1/4 space-y-4">
          <div className="bg-white rounded-xl shadow-md p-4 dark:bg-gray-800 dark:shadow-none">
            <h3 className="text-lg font-semibold text-gray-800 mb-3 dark:text-gray-300">
              Quick Actions
            </h3>
            <div className="space-y-2">
              {[
                { text: "Create an itinerary", icon: "📝" },
                { text: "Find destinations", icon: "🔍" },
                { text: "Travel tips", icon: "💡" },
                { text: "Visa information", icon: "🛂" },
              ].map((action, index) => (
                <button
                  key={index}
                  onClick={() => handlePromptClick(action.text)}
                  disabled={isLoading}
                  className="flex items-center w-full p-3 rounded-lg text-left transition-all hover:bg-blue-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-400 hover:cursor-pointer"
                >
                  <span className="text-xl mr-3">{action.icon}</span>
                  <span className="text-sm">{action.text}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-4 dark:bg-gray-800 dark:shadow-none">
            <h3 className="text-lg font-semibold text-gray-800 mb-3 dark:text-gray-300">
              Popular Destinations
            </h3>
            <div className="space-y-2">
              {destinations.map((destination, index) => (
                <button
                  key={index}
                  onClick={() =>
                    handlePromptClick(
                      `Create a 3-day itinerary for ${destination.text}`
                    )
                  }
                  disabled={isLoading}
                  className="flex items-center w-full p-2 rounded-lg text-left transition-all hover:bg-blue-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-400 hover:cursor-pointer"
                >
                  <Image
                    src={destination.img}
                    alt={destination.text}
                    width={32}
                    height={32}
                    className="rounded-md mr-3 object-cover"
                  />
                  <span className="text-sm">{destination.text}</span>
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* Main chat area */}
        <div className="flex-grow flex flex-col md:w-3/4">
          {/* Welcome section - only visible at start */}
          <AnimatePresence>
            {showWelcome && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="mb-6"
              >
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-gray-800 mb-2 dark:text-gray-300">
                    Welcome to AI Trip Maker Chat
                  </h2>
                  <p className="text-gray-600 dark:text-gray-500">
                    Your personal AI travel assistant ready to help plan your
                    perfect trip.
                  </p>
                </div>

                <DestinationShowcase onDestinationClick={handlePromptClick} />
                <TravelPromptSuggestions
                  onPromptClick={handlePromptClick}
                  isLoading={isLoading}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Chat messages */}
          <div
            ref={chatContainerRef}
            className={`flex-grow overflow-y-auto pr-2 custom-scrollbar ${
              showWelcome ? "max-h-[40vh]" : "max-h-[calc(100vh-220px)]"
            } mb-4`}
          >
            <AnimatePresence>
              {messages.map((message) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className={`mb-4 ${
                    message.role === "user"
                      ? "flex justify-end"
                      : "flex justify-start"
                  }`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                      message.role === "user"
                        ? "bg-blue-600 text-white rounded-tr-none"
                        : "bg-white dark:bg-gray-700 rounded-tl-none shadow-md dark:shadow-none"
                    }`}
                  >
                    <div
                      className={
                        message.role === "assistant"
                          ? "prose prose-sm dark:prose-invert"
                          : ""
                      }
                      dangerouslySetInnerHTML={{ __html: message.content }}
                    />
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

            {/* Loading indicator */}
            {isLoading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex items-center mb-4"
              >
                <div className="bg-white dark:bg-gray-700 rounded-2xl px-4 py-3 shadow-md dark:shadow-none rounded-tl-none flex items-center">
                  <span className="mr-2 text-gray-700 dark:text-gray-400">
                    Thinking
                  </span>
                  <span className="flex space-x-1">
                    <motion.span
                      className="h-1.5 w-1.5 bg-gray-400 dark:bg-gray-500 rounded-full"
                      animate={{ scale: [1, 1.5, 1] }}
                      transition={{
                        repeat: Infinity,
                        duration: 1,
                        repeatDelay: 0,
                      }}
                    />
                    <motion.span
                      className="h-1.5 w-1.5 bg-gray-400 dark:bg-gray-500 rounded-full"
                      animate={{ scale: [1, 1.5, 1] }}
                      transition={{
                        repeat: Infinity,
                        duration: 1,
                        delay: 0.3,
                        repeatDelay: 0,
                      }}
                    />
                    <motion.span
                      className="h-1.5 w-1.5 bg-gray-400 dark:bg-gray-500 rounded-full"
                      animate={{ scale: [1, 1.5, 1] }}
                      transition={{
                        repeat: Infinity,
                        duration: 1,
                        delay: 0.6,
                        repeatDelay: 0,
                      }}
                    />
                  </span>
                </div>
              </motion.div>
            )}
          </div>

          {/* Input area */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md dark:shadow-none px-4 py-3">
            <form
              onSubmit={handleSubmit}
              className="flex flex-col w-full gap-2"
            >
              <AnimatePresence>
                {errorMessage && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                    className="text-red-600 text-sm bg-red-100 dark:bg-red-900 dark:text-red-400 rounded-md px-4 py-2"
                  >
                    {errorMessage}
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={handleInputChange}
                  placeholder="Ask me anything about travel..."
                  className="flex-grow bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:bg-white dark:focus:bg-gray-600 transition-all"
                  disabled={isLoading}
                />
                <button
                  type="submit"
                  disabled={isLoading || !input.trim()}
                  className="bg-blue-600 text-white rounded-lg p-3 hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </form>
          </div>

          {/* Mobile action buttons - only visible on small screens */}
          <div className="md:hidden mt-4 grid grid-cols-2 gap-2">
            <button
              onClick={() => handlePromptClick("Create an itinerary")}
              disabled={isLoading}
              className="bg-white dark:bg-gray-800 shadow-md dark:shadow-none rounded-lg p-3 flex items-center justify-center text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-gray-700 transition-colors"
            >
              <span className="mr-2">📝</span>
              <span className="text-sm">Create Itinerary</span>
            </button>
            <button
              onClick={() => handlePromptClick("Find destinations")}
              disabled={isLoading}
              className="bg-white dark:bg-gray-800 shadow-md dark:shadow-none rounded-lg p-3 flex items-center justify-center text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-gray-700 transition-colors"
            >
              <span className="mr-2">🔍</span>
              <span className="text-sm">Find Destinations</span>
            </button>
          </div>
        </div>
      </main>

      {/* Add custom scrollbar styles */}
      <style jsx global>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: rgba(0, 0, 0, 0.1);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-color: rgba(0, 0, 0, 0.2);
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: rgba(255, 255, 255, 0.1);
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-color: rgba(255, 255, 255, 0.2);
        }
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
}
